/** @jsxImportSource theme-ui */
import { ResponsiveStyleValue } from '@theme-ui/css';
import React, { useCallback, useMemo } from 'react';
import { useBreakpointValue } from '../../hooks';
import FlexContainer, { FlexContainerProps } from '../FlexContainer';
import { FlexDirection, SpaceSizing } from '../utils/types';

export interface StackProps extends Omit<FlexContainerProps, 'direction' | 'wrap' | 'shadow'> {
    direction?: FlexDirection;
    spacing?: ResponsiveStyleValue<SpaceSizing>;
    itemFlex?: ResponsiveStyleValue<string>;
}

type StackDividerProps = Omit<StackProps, 'children'>;

function StackItem({ itemFlex, children }: Omit<StackProps, 'direction' | 'spacing'>): JSX.Element {
    return (
        <div sx={{ height: 'auto', display: 'inline-block', flex: itemFlex, minWidth: 0, maxWidth: '100%' }}>
            {children}
        </div>
    );
}
StackItem.defaultProps = {
    itemFlex: '0 0 auto',
};

function StackDivider({ spacing, direction }: StackDividerProps): JSX.Element {
    const spacingStyles = (() => {
        switch (direction) {
            case 'row':
                return {
                    marginRight: spacing,
                };
            case 'row-reverse':
                return {
                    marginLeft: spacing,
                };
            case 'column-reverse':
                return {
                    marginTop: spacing,
                };
            case 'column':
            default:
                return {
                    marginBottom: spacing,
                };
        }
    })();
    return (
        <div
            sx={{
                ...spacingStyles,
                borderWidth: 0,
                alignSelf: 'stretch',
                borderColor: 'inherit',
                width: 'auto',
                height: 'auto',
            }}
        />
    );
}

export default function Stack({ spacing, direction, itemFlex, children, ...otherProps }: StackProps): JSX.Element {
    const directionResponsive = useBreakpointValue(direction);
    const addSpaceBetween = useCallback(
        (elements: any[]) => {
            const filteredList = elements.filter(Boolean);
            return filteredList.reduce(
                (newChildren: Array<React.ReactNode>, child: React.ReactNode | React.ReactNodeArray, index: number) => {
                    if (!child) {
                        return newChildren;
                    }
                    const isLast = index + 1 === filteredList.length;
                    newChildren.push(
                        <StackItem itemFlex={itemFlex} key={`${index}-item`}>
                            {child}
                        </StackItem>
                    );
                    if (!isLast) {
                        newChildren.push(<StackDivider {...{ spacing, direction: directionResponsive }} key={index} />);
                    }
                    return newChildren;
                },
                []
            );
        },
        [spacing, directionResponsive]
    );

    const stackedChildren = useMemo(() => {
        if (!children) {
            return [];
        }
        return addSpaceBetween(Array.isArray(children) ? children : [children]);
    }, [addSpaceBetween, children]);

    return (
        <FlexContainer wrap="wrap" {...{ ...otherProps, direction: directionResponsive }}>
            {stackedChildren}
        </FlexContainer>
    );
}

Stack.defaultProps = {
    direction: 'column',
    spacing: 'medium',
};
